import * as CL from '@design-system/component-library';
import { imageUrl } from '../../common/constants/ProductOfferingConstants.js';
import { dsClass } from '../../common/constants/dsClasses';
import { isDeviceEppSubcategory } from '../../common/enums.js';
import { addMsg, quantityMsg, removeMsg, subtractMsg, t } from '../../common/i18n';
import { eurosToCents } from '../../common/utils/priceUtils.js';
import { getLocalizedString, getMaxProductSelection } from '../../common/utils/productOfferingsUtils.js';
import { PriceType } from '../HorizontalSubscriptionCard/types.js';
import { Picture } from '../Picture/Picture.js';
import { PlanProductRuntimeCharacteristic } from '../PlaceOrder/utils/characteristicsFlagsUtilsCoCo.js';
import { getProductOfferAddOnPrices } from '../PlaceOrder/utils/placeOrderUtilsCoCo';
import {
  getCharacteristicOptions,
  getSelectedCharacteristicValue,
} from '../PlaceOrder/utils/runtimeCharacteristicsCoCo.js';
import { BundleAddonGroupSelectorCoCo } from '../ProductOfferingAddonCoCo/BundleAddonGroupSelectorCoCo.js';
import { CommercialAddonsSectionCoCo } from '../ProductOfferingAddonCoCo/CommercialAddonsSectionCoCo.js';
import { ProductOfferAddOn } from '../ProductOfferingAddonCoCo/ProductOfferAddOnCoCo.js';
import { isInstallationServiceGroup } from '../ProductOfferingModalCoCo/FixedBroadbandConfigCoCoUtils.js';
import { getGroupOfferingPriceInEuros } from '../ProductOfferingModalCoCo/ProductOfferingsModalCoCoUtils.js';
import { Price } from '../ToggleCheckbox/ToggleCheckbox.js';
import { DisclaimerFields } from './DisclaimerFields.js';
import { ShoppingBasketEntry } from './ShoppingBasketEntry.js';
import {
  getCheckedAddonKey,
  getEffectivePriceForItem,
  getEffectivePrices,
  getPriceDataForBasketItem,
  getProductPriceToDisplay,
  getSelectedOfferingIds,
  isFixedLineEquipmentGroup,
} from './shoppingBasketHelpersCoCo.js';
import { getOnetimePaymentText } from './shoppingBasketUtils.js';
import type { ProductOfferingCartItems } from '../../common/types/shoppingBasketCoCo.js';
import type { CartBundleAddonGroup } from '../../generated/api/cartBundleAddonGroup.js';

interface ShoppingBasketItemsCoCoProps {
  basketItems: ProductOfferingCartItems[];
  onQuantityChange: (newQty: number, groupItems: ProductOfferingCartItems[]) => void;
  onAddOnChange: (addonId: string, groupCartItemIds: string[]) => void;
  onAddOnGroupSelectionChange?: (groupCartItemIds: string[], groupId: string, selectedOfferingIds: string[]) => void;
  onRuntimeCharacteristicChange?: (groupCartItemIds: string[], selectedValue: string) => void;
}

function renderBundleAddonGroup(
  addonGroup: CartBundleAddonGroup,
  basketItem: ProductOfferingCartItems,
  groupCartItemIds: string[],
  onAddOnGroupSelectionChange?: (groupCartItemIds: string[], groupId: string, selectedOfferingIds: string[]) => void
) {
  const selectedOfferingIds = getSelectedOfferingIds(addonGroup, basketItem);
  return (
    <BundleAddonGroupSelectorCoCo
      key={addonGroup.id}
      group={addonGroup}
      selectedOfferingIds={selectedOfferingIds}
      onSelectionChange={(groupId, selectedIds) =>
        onAddOnGroupSelectionChange?.(groupCartItemIds, groupId, selectedIds)
      }
    />
  );
}

function renderDeviceGroup(
  addonGroup: CartBundleAddonGroup,
  basketItem: ProductOfferingCartItems,
  groupCartItemIds: string[],
  onAddOnGroupSelectionChange?: (groupCartItemIds: string[], groupId: string, selectedOfferingIds: string[]) => void
) {
  const selectedOfferingIds = getSelectedOfferingIds(addonGroup, basketItem);
  const offerings = addonGroup.productOffering ?? [];
  // Use only the selection from the modal - do not default to first offering
  const selectedValue = selectedOfferingIds[0] ?? '';
  const dropdownItems = offerings.map(offering => ({
    label: getLocalizedString(offering.name),
    value: offering.id,
  }));
  const selectedOffering = addonGroup.productOffering?.find(offering => offering.id === selectedValue);

  return (
    <div className={`${dsClass.MARGIN_TOP_2}`} key={addonGroup.id}>
      <div
        className={`${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID} ${dsClass.PADDING_2} ${dsClass.GRID} bundle-addon-group`}
      >
        <div className={`${dsClass.GRID_ROW}`}>
          <div
            className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
          >
            <CL.Dropdown
              items={dropdownItems}
              selectedValue={selectedValue}
              label={getLocalizedString(addonGroup.name)}
              onValueChange={(element: HTMLLIElement) => {
                const value = element.dataset.value;
                if (value) {
                  onAddOnGroupSelectionChange?.(groupCartItemIds, addonGroup.id, [value]);
                }
              }}
            />
          </div>
          <div
            className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
          >
            <Price
              priceInfo={getProductOfferAddOnPrices(
                getGroupOfferingPriceInEuros(selectedOffering?.productOfferingPrice)
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function shoppingBasketItemsCoCo({
  basketItems,
  onQuantityChange,
  onAddOnChange,
  onAddOnGroupSelectionChange,
  onRuntimeCharacteristicChange,
}: ShoppingBasketItemsCoCoProps) {
  // Group entries by productId, addons and payment selection so different device plans stay separate.
  const groups = basketItems.reduce<Map<string, ProductOfferingCartItems[]>>((acc, item) => {
    const key = `${item.id}|${getCheckedAddonKey(item)}|${item.employeeProductList?.uuid ?? ''}|${item.employeeProductList?.accountMdmId ?? ''}`;
    const existing = acc.get(key) ?? [];
    acc.set(key, [...existing, item]);
    return acc;
  }, new Map());

  return Array.from(groups.values()).map(group => {
    const basketItem = group[0];
    const qty = group.length;
    const subcategory = basketItem.productOfferingType?.subcategory || '';

    const quantityField = (
      <CL.Quantity
        i18n_quantity_deleteAriaLabel={t.R3VE(removeMsg)}
        i18n_quantity_minusAriaLabel={t.C2KQ(subtractMsg)}
        i18n_quantity_plusAriaLabel={t.VKFM(addMsg)}
        i18n_quantity_quantityAriaLabel={t.M0W7(quantityMsg)}
        className={dsClass.SHOPPINGCART_PRODUCT_QUANTITY}
        currentValue={qty}
        minValue={1}
        maxValue={getMaxProductSelection(subcategory)}
        onChange={(newQty: number) => {
          onQuantityChange(newQty, group);
        }}
        onRemove={() => {
          onQuantityChange(0, group);
        }}
        removeLessThanMin
      />
    );

    const bundleAddonGroups = basketItem.bundleAddonGroup ?? [];
    const deviceGroups = bundleAddonGroups.filter(addonGroup => isFixedLineEquipmentGroup(addonGroup));
    const installationServiceGroups = bundleAddonGroups.filter(isInstallationServiceGroup);
    const serviceGroups = bundleAddonGroups.filter(
      addonGroup => !isFixedLineEquipmentGroup(addonGroup) && !isInstallationServiceGroup(addonGroup)
    );
    const groupOfferingIds = new Set(
      bundleAddonGroups.flatMap(addonGroup => (addonGroup.productOffering ?? []).map(offering => offering.id))
    );
    const standaloneAddons = (basketItem.bundledAddonProductOffering ?? []).filter(
      addon => !groupOfferingIds.has(addon.id)
    );
    // Show every available commercial add-on so the user can still select the unselected ones.
    const commercialAddons = basketItem.commercialAddonProductOffering ?? [];
    const runtimeCharacteristicOptions = getCharacteristicOptions(
      basketItem.planProductOffering?.productOfferingRuntimeCharacteristic,
      PlanProductRuntimeCharacteristic.FIBER_MODE
    );
    const selectedRuntimeCharacteristicValue = getSelectedCharacteristicValue(
      basketItem.productOfferingRuntimeCharacteristic,
      PlanProductRuntimeCharacteristic.FIBER_MODE
    );
    const hasRuntimeCharacteristic = runtimeCharacteristicOptions.length > 0;

    const hasAnyAddons =
      hasRuntimeCharacteristic ||
      standaloneAddons.length > 0 ||
      bundleAddonGroups.length > 0 ||
      commercialAddons.length > 0;

    const groupCartItemIds = group.map(item => item.cartItemId);

    const renderAddonRow = (addon: (typeof standaloneAddons)[0]) => (
      <div className={`${dsClass.MARGIN_TOP_3} ${dsClass.MARGIN_BOTTOM_3}`} key={addon.id}>
        <ProductOfferAddOn
          key={addon.id}
          linkInfo={{
            id: addon.id,
            linkInfoDescription: getLocalizedString(addon.description),
          }}
          initialValue={addon.isChecked}
          id={addon.id}
          handleAddOnChange={(id: string) => {
            onAddOnChange(id, groupCartItemIds);
          }}
          productOffering={basketItem}
          priceInfo={getProductOfferAddOnPrices(
            addon.productOfferingPrice?.find(p => p.priceType === PriceType.RECURRING_CHARGE)?.priceWithoutTax ?? 0
          )}
          title={addon.name.value ?? ''}
          disabled={addon.isDefault}
        />
      </div>
    );

    const addons = hasAnyAddons ? (
      <div>
        {/* Device groups (CPE equipment) render without a section header */}
        {deviceGroups.map(addonGroup =>
          renderDeviceGroup(addonGroup, basketItem, groupCartItemIds, onAddOnGroupSelectionChange)
        )}
        {(standaloneAddons.length > 0 || serviceGroups.length > 0 || hasRuntimeCharacteristic) && (
          <>
            {(standaloneAddons.length > 0 || serviceGroups.length > 0 || hasRuntimeCharacteristic) && (
              <div className={`${dsClass.FONT_WEIGHT_700} ${dsClass.MARGIN_BOTTOM_2} ${dsClass.MARGIN_TOP_3}`}>
                {t.UU5G('Select additional services')}
              </div>
            )}
            {standaloneAddons.map(renderAddonRow)}
            {hasRuntimeCharacteristic && (
              <div className={`${dsClass.MARGIN_TOP_2}`}>
                <div
                  className={`${dsClass.BORDER_WIDTH_1} ${dsClass.BORDER_STYLE_SOLID} ${dsClass.PADDING_2} ${dsClass.GRID} bundle-addon-group`}
                >
                  <div className={`${dsClass.GRID_ROW}`}>
                    <div
                      className={`${dsClass.GRID_COL_L_6} ${dsClass.GRID_COL_S_6} ${dsClass.GRID_COL_M_6} ${dsClass.GRID_COL_XS_4}`}
                    >
                      <CL.Dropdown
                        items={runtimeCharacteristicOptions}
                        selectedValue={selectedRuntimeCharacteristicValue}
                        label={t.RZQ4('Fiber type')}
                        onValueChange={(element: HTMLLIElement) => {
                          const value = element.dataset.value;
                          if (value) {
                            onRuntimeCharacteristicChange?.(groupCartItemIds, value);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {serviceGroups.map(addonGroup =>
              renderBundleAddonGroup(addonGroup, basketItem, groupCartItemIds, onAddOnGroupSelectionChange)
            )}
          </>
        )}
        {installationServiceGroups.length > 0 &&
          installationServiceGroups.map(addonGroup =>
            renderBundleAddonGroup(addonGroup, basketItem, groupCartItemIds, onAddOnGroupSelectionChange)
          )}
        <CommercialAddonsSectionCoCo
          addons={commercialAddons}
          subcategory={subcategory}
          quantity={qty}
          onAddOnChange={(id: string) => onAddOnChange(id, groupCartItemIds)}
          productOffering={basketItem}
          className={isDeviceEppSubcategory(subcategory) ? 'of-shopping-basket-device-commercial-addons' : undefined}
          itemClassName={`${dsClass.MARGIN_TOP_3} ${dsClass.MARGIN_BOTTOM_3}`}
        />
      </div>
    ) : (
      <></>
    );

    const effectivePrices = getEffectivePrices(basketItem);
    const productPrice = getProductPriceToDisplay(effectivePrices, qty);

    const priceData = getPriceDataForBasketItem(basketItem);

    const oneTimePaymentText = getOnetimePaymentText(
      eurosToCents(getEffectivePriceForItem(basketItem, PriceType.ONE_TIME_CHARGE)),
      qty,
      true
    );

    const priceDisclaimers = (
      <DisclaimerFields oneTimePaymentText={oneTimePaymentText} priceData={priceData} quantity={qty} />
    );

    const priceElement = (
      <>
        <output className={dsClass.SHOPPINGCART_PRICE_AMOUNT}>{productPrice.amount}</output>
        <span className={dsClass.SHOPPINGCART_PRICE_UNIT}>{productPrice.unit}</span>
      </>
    );

    const paymentDisplayField = <div className={dsClass.SHOPPINGCART_PRODUCT_PRICE_OPTIONS} />;

    const productName = getLocalizedString(basketItem.name);
    const productDescription = getLocalizedString(basketItem.description ?? '');

    return (
      <ShoppingBasketEntry
        key={basketItem.cartItemId}
        id={basketItem.id}
        productImage={
          <Picture
            alt={productName}
            offerWidthAlternatives={[128]}
            renderedImageSize={{ onPhone: '64px' }}
            src={imageUrl}
          />
        }
        productName={<>{productName}</>}
        productDescription={<>{productDescription}</>}
        paymentField={paymentDisplayField}
        quantityField={quantityField}
        price={<> {priceElement} </>}
        addOns={addons}
        disclaimers={priceDisclaimers}
      />
    );
  });
}
